import { Close, Edit } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Row, Stack } from 'react-bootstrap';
import { useNotification } from '../../../../hooks/NotificationContext';
import {
  formatCommentWithAccountIds,
  formatCommentWithDisplayNames,
  getDateAndTime,
  renderCommentBody,
} from '../../../../lib/utils';
import {
  deleteComment,
  updateComment,
} from '../../../../modules/entity-details';
import ConfirmationModal from '../../ConfirmationModal';
import CustomAvatar from '../../CustomAvatar';
import CommentInput from './CommentInput';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import { getDisplayUserName } from '../../../../lib/userUtils';

const SingleComment = ({ comment, fetchData, users, entityKey, otherData }) => {
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const commentDate = getDateAndTime(comment.updated_at);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment.body);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taggedUsers, setTaggedUsers] = useState(comment?.mentions || []);
  const { addNotification } = useNotification();

  const isEdited = () => {
    return (
      new Date(comment.updated_at).getTime() > new Date(comment.created_at)
    );
  };
  const deleteSingleComment = async () => {
    setLoading(true);
    try {
      await deleteComment(comment.id);
      fetchData();
      addNotification('success', 'Comment deleted successfully!');
    } catch (error) {
      addNotification('danger', 'Failed to delete comment.');
    } finally {
      setLoading(false);
    }
  };
  const updateSingleComment = async (newComment) => {
    setLoading(true);
    const mentions = [...new Set(taggedUsers)];
    const commentBody = formatCommentWithAccountIds(newComment, users);
    try {
      let additionalData = {};
      const {
        mentions: existingMentions = [],
        user,
        id: commentId,
        gainss_project_id: projectId,
        entity_type: entityType,
      } = comment;
      const newMentions = mentions?.filter(
        (userId) => !existingMentions.includes(userId)
      );
      if (newMentions && newMentions.length > 0) {
        const notificationLink =
          generateNotificationUrl(
            entityType,
            projectId,
            entityKey,
            'comments',
            otherData
          ) || '#';

        additionalData = {
          commentorName: getDisplayUserName(user),
          entityKey,
          entityLink: notificationLink,
          newMentions,
        };
      }
      const payload = {
        body: commentBody.trim(),
        mentions: mentions,
        additionalData,
      };
      await updateComment(commentId, { comment: payload });

      setIsEditing(false);
      fetchData();
      addNotification('success', 'Comment updated successfully!');
    } catch (error) {
      addNotification('danger', error.message || 'Failed to update comment.');
    } finally {
      setLoading(false);
    }
  };
  const handleTextInputChange = (newValue) => {
    setEditedComment(newValue);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedComment(comment.body);
  };
  useEffect(() => {
    setEditedComment(formatCommentWithDisplayNames(comment.body, users));
  }, [comment, users]);

  return (
    <>
      <div className="comment-container">
        <Row>
          <Col xs="auto">
            <CustomAvatar
              firstName={comment.user?.first_name}
              lastName={comment.user?.last_name}
              showTitle={false}
            />
          </Col>
          <Col>
            <p>
              <strong>{getDisplayUserName(comment?.user)}</strong> has added a comment{' '}
              {isEdited() ? '(Edited!)' : ''} - {commentDate}
            </p>
          </Col>
          {currentUser && currentUser.id === comment?.user?.id && (
            <Col xs="auto">
              <Stack direction="horizontal" gap={2}>
                <Badge
                  bg="info"
                  style={{ cursor: 'pointer' }}
                  pill
                  onClick={() => setIsEditing(!isEditing)}
                  title="Edit comment">
                  <Edit fontSize="sm" />
                </Badge>
                <Badge
                  bg="danger"
                  pill
                  style={{ cursor: 'pointer' }}
                  title="Remove comment"
                  onClick={() => setShowConfirm(true)}>
                  <Close fontSize="sm" />
                </Badge>
              </Stack>
            </Col>
          )}
        </Row>

        <Row style={{ marginLeft: '40px' }}>
          <Col className="mb-2">
            {isEditing ? (
              <CommentInput
                users={users}
                onCommentChange={handleTextInputChange}
                comment={editedComment}
                error={error}
                setError={setError}
                setTaggedUsers={setTaggedUsers}
              />
            ) : (
              <p>{renderCommentBody(comment.body, users)}</p>
            )}
          </Col>
        </Row>

        {isEditing && (
          <Row style={{ marginLeft: '40px' }}>
            <Col className="text-end">
              <Button
                onClick={() => updateSingleComment(editedComment)}
                variant="primary"
                size="sm"
                disabled={loading || !editedComment}
                style={{ marginRight: '0.5rem' }}>
                Save
              </Button>
              <Button
                onClick={handleCancel}
                size="sm"
                variant="outline-secondary">
                Cancel
              </Button>
            </Col>
          </Row>
        )}
        <hr />
      </div>

      <ConfirmationModal
        show={showConfirm}
        handleConfirm={deleteSingleComment}
        handleClose={() => setShowConfirm(false)}
        title="Remove comment"
        body={'Are you sure you want to remove this comment?'}
        loading={loading}
      />
    </>
  );
};

export default SingleComment;
